import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, Plus, Trash2, Download, Upload } from "lucide-react";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import LangToggle, { DraftNote } from "../components/LangToggle";
import useLang from "../lib/lang";
import useLocalState from "../lib/useLocalState";

const OVERLINE_CLS = "text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold";
const TH_CLS =
  "text-left px-3 py-2 text-[11px] font-accent uppercase tracking-[0.12em] text-ink-muted border border-rule bg-linen-alt print:bg-white print:text-black";
const CELL_INPUT =
  "w-full bg-white border border-rule px-2 py-1.5 text-[13px] leading-snug text-ink placeholder:text-ink-muted/40 focus:outline-none focus:border-ink print:border-0 print:p-1";
const CELL_TEXTAREA =
  "w-full bg-white border border-rule px-2 py-1.5 text-[13px] leading-snug text-ink placeholder:text-ink-muted/40 focus:outline-none focus:border-ink print:border-0 print:p-1 resize-y";

const BLANK_ROW = () => ({
  id: Math.random().toString(36).slice(2, 10),
  date: "",
  who: "",
  kind: "",
  what: "",
  followup: "",
});

function toCsv(rows) {
  const header = ["Date", "Who", "Type", "What was said or agreed", "Follow-up"];
  const esc = (s) => {
    const v = String(s ?? "").replace(/"/g, '""');
    return /[",\n]/.test(v) ? `"${v}"` : v;
  };
  const lines = [header.join(",")];
  rows.forEach((r) => {
    lines.push([r.date, r.who, r.kind, r.what, r.followup].map(esc).join(","));
  });
  return lines.join("\n");
}

function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
    } else {
      field += c;
    }
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

const isBlankRow = (r) => !(r.date || r.who || r.kind || r.what || r.followup);

export default function ChronologyPage() {
  const [lang, setLang] = useLang();
  const H =
    lang === "cy"
      ? {
          date: "Dyddiad",
          who: "Pwy",
          kind: "Math",
          what: "Beth ddywedwyd neu gytunwyd",
          followup: "Camau dilynol",
          label: "Cyfeirnod neu deitl yr achos",
          heading: "Cronoleg",
        }
      : {
          date: "Date",
          who: "Who",
          kind: "Type",
          what: "What was said or agreed",
          followup: "Follow-up",
          label: "Case reference or title",
          heading: "Chronology",
        };
  const [caseTitle, setCaseTitle, clearTitle] = useLocalState(
    "yana:chron:title:v1",
    ""
  );
  const [rows, setRows, clearRows] = useLocalState("yana:chron:rows:v1", [
    BLANK_ROW(),
    BLANK_ROW(),
    BLANK_ROW(),
  ]);

  const fileRef = useRef(null);
  const [importStatus, setImportStatus] = useState(null);

  const handleImportFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        let parsed = parseCsv(String(reader.result));
        if (parsed.length && /^date$/i.test((parsed[0][0] || "").trim())) {
          parsed = parsed.slice(1);
        }
        const imported = parsed
          .map((r) => ({
            ...BLANK_ROW(),
            date: r[0] || "",
            who: r[1] || "",
            kind: r[2] || "",
            what: r[3] || "",
            followup: r[4] || "",
          }))
          .filter((r) => !isBlankRow(r));
        if (!imported.length) {
          setImportStatus({
            ok: false,
            msg: "No entries found in that file. It should be a .csv downloaded from this page — five columns: date, who, type, what, follow-up.",
          });
          return;
        }
        setRows((rs) => {
          const kept = [...rs];
          while (kept.length && isBlankRow(kept[kept.length - 1])) kept.pop();
          return [...kept, ...imported];
        });
        setImportStatus({
          ok: true,
          msg: `${imported.length} ${imported.length === 1 ? "entry" : "entries"} imported — added below your existing rows.`,
        });
      } catch {
        setImportStatus({ ok: false, msg: "That file could not be read as a .csv." });
      }
    };
    reader.onerror = () =>
      setImportStatus({ ok: false, msg: "That file could not be read. Please try again." });
    reader.readAsText(file);
  };

  const updateRow = (id, field, value) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const addRow = () => setRows((rs) => [...rs, BLANK_ROW()]);
  const deleteRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  const clearAll = () => {
    if (window.confirm("Clear everything from this chronology? This cannot be undone.")) {
      clearTitle();
      clearRows();
    }
  };

  const downloadCsv = () => {
    const csv = toCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safeTitle = (caseTitle || "chronology")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40);
    a.href = url;
    a.download = `${safeTitle || "chronology"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const hasContent = useMemo(
    () => !!caseTitle || rows.some((r) => r.date || r.who || r.kind || r.what || r.followup),
    [caseTitle, rows]
  );

  return (
    <div data-testid="chronology-page" className="chronology">
      <PageMeta
        title="Chronology — dated record of every meeting, letter and call"
        description="A tiny printable table you can fill in day by day, so that when it matters you have a dated record of every meeting, letter and phone call. Saves in your browser, prints cleanly on A4."
        path="/understanding/records/chronology"
      />
      <PageHeader
        overline="Records · Chronology"
        title="Keep the record they cannot."
        lede="A social work file rarely contains every conversation. Yours can. Fill in one line the moment something happens — a phone call, a letter, a meeting — and by the time it matters you will have what nobody else has: a dated record in your own hand."
      />

      <article className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 print:p-0 print:max-w-full">
        {/* Toolbar — hidden on print */}
        <div className="print:hidden flex items-center justify-between gap-4 mb-8 flex-wrap">
          <Link
            to="/understanding/records"
            data-testid="chronology-back-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back to the full guide
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <LangToggle lang={lang} setLang={setLang} />
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportFile}
              className="hidden"
              data-testid="chronology-import-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              data-testid="chronology-import-csv"
              className="inline-flex items-center gap-2 border border-ink/30 text-ink px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
            >
              <Upload size={13} strokeWidth={1.75} />
              Import .csv
            </button>
            <button
              type="button"
              onClick={downloadCsv}
              data-testid="chronology-download-csv"
              className="inline-flex items-center gap-2 border border-ink/30 text-ink px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
            >
              <Download size={13} strokeWidth={1.75} />
              Download .csv
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="chronology-print-button"
              className="inline-flex items-center gap-2 bg-ink text-linen px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:bg-river transition-colors"
            >
              <Printer size={13} strokeWidth={1.75} />
              Print or save as PDF
            </button>
          </div>
        </div>

        {importStatus && (
          <p
            data-testid="chronology-import-status"
            role="status"
            className={`print:hidden -mt-4 mb-6 text-sm font-accent ${importStatus.ok ? "text-river" : "text-ember"}`}
          >
            {importStatus.msg}
          </p>
        )}

        {lang === "cy" && (
          <div className="mb-6 -mt-2">
            <DraftNote lang={lang} />
          </div>
        )}

        {/* Title / meta row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 print:mb-4 print:gap-4 print:grid-cols-3">
          <div className="md:col-span-2">
            <label className={"block text-xs font-accent uppercase tracking-[0.14em] text-ink-muted mb-1.5"} htmlFor="chron-title">
              {H.label}
            </label>
            <input
              id="chron-title"
              data-testid="chronology-title"
              type="text"
              value={caseTitle}
              onChange={(e) => setCaseTitle(e.target.value)}
              placeholder="e.g. Oxfordshire Children's Services — 2026"
              className="w-full bg-white border border-rule px-3 py-2 text-base text-ink placeholder:text-ink-muted/50 focus:outline-none focus:border-ink"
            />
          </div>
          <div className="md:text-right md:self-end">
            <div className={OVERLINE_CLS + " print:text-black"}>{H.heading}</div>
            <LastReviewed path="/understanding/records/chronology" className="mt-1 print:hidden" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto print:overflow-visible">
          <table
            data-testid="chronology-table"
            className="w-full border-collapse text-sm print:text-[10pt]"
          >
            <thead>
              <tr>
                <th className={TH_CLS + " w-[112px]"}>{H.date}</th>
                <th className={TH_CLS + " w-[160px]"}>{H.who}</th>
                <th className={TH_CLS + " w-[110px]"}>{H.kind}</th>
                <th className={TH_CLS}>{H.what}</th>
                <th className={TH_CLS + " w-[140px]"}>{H.followup}</th>
                <th className="w-8 print:hidden border border-rule bg-linen-alt" aria-hidden />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} data-testid={`chronology-row-${idx}`} className="align-top">
                  <td className="border border-rule p-1 print:p-0">
                    <input
                      data-testid={`chron-date-${idx}`}
                      type="text"
                      value={r.date}
                      onChange={(e) => updateRow(r.id, "date", e.target.value)}
                      placeholder="12/02/2026"
                      className={CELL_INPUT}
                    />
                  </td>
                  <td className="border border-rule p-1 print:p-0">
                    <input
                      data-testid={`chron-who-${idx}`}
                      type="text"
                      value={r.who}
                      onChange={(e) => updateRow(r.id, "who", e.target.value)}
                      placeholder="J. Smith, social worker"
                      className={CELL_INPUT}
                    />
                  </td>
                  <td className="border border-rule p-1 print:p-0">
                    <input
                      data-testid={`chron-kind-${idx}`}
                      type="text"
                      value={r.kind}
                      onChange={(e) => updateRow(r.id, "kind", e.target.value)}
                      placeholder="Call / Meeting / Letter"
                      className={CELL_INPUT}
                    />
                  </td>
                  <td className="border border-rule p-1 print:p-0">
                    <textarea
                      data-testid={`chron-what-${idx}`}
                      rows={2}
                      value={r.what}
                      onChange={(e) => updateRow(r.id, "what", e.target.value)}
                      placeholder="Brief but factual. Direct quotes where you can."
                      className={CELL_TEXTAREA}
                    />
                  </td>
                  <td className="border border-rule p-1 print:p-0">
                    <input
                      data-testid={`chron-followup-${idx}`}
                      type="text"
                      value={r.followup}
                      onChange={(e) => updateRow(r.id, "followup", e.target.value)}
                      placeholder="by 19/02"
                      className={CELL_INPUT}
                    />
                  </td>
                  <td className="w-8 border-0 print:hidden align-middle text-center">
                    <button
                      type="button"
                      onClick={() => deleteRow(r.id)}
                      aria-label={`Remove row ${idx + 1}`}
                      data-testid={`chron-delete-${idx}`}
                      className="text-ink-muted/60 hover:text-ember p-1 transition-colors"
                    >
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
              {/* Extra empty rows appear only on print — for handwriting on the paper */}
              {Array.from({ length: Math.max(0, 12 - rows.length) }).map((_, i) => (
                <tr key={`blank-${i}`} className="hidden print:table-row">
                  <td className="border border-black h-8" />
                  <td className="border border-black h-8" />
                  <td className="border border-black h-8" />
                  <td className="border border-black h-8" />
                  <td className="border border-black h-8" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add row / clear all */}
        <div className="print:hidden mt-4 flex items-center justify-between gap-4 flex-wrap">
          <button
            type="button"
            onClick={addRow}
            data-testid="chronology-add-row"
            className="inline-flex items-center gap-2 border border-ink/30 text-ink px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
          >
            <Plus size={13} strokeWidth={1.75} />
            Add row
          </button>
          {hasContent && (
            <button
              type="button"
              onClick={clearAll}
              data-testid="chronology-clear-all"
              className="inline-flex items-center gap-2 text-xs font-accent text-ink-muted hover:text-ember transition-colors"
            >
              <Trash2 size={12} strokeWidth={1.75} />
              Clear everything
            </button>
          )}
        </div>

        {/* Guidance — hidden on print */}
        <section className="print:hidden mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-rule bg-linen-alt p-6">
            <div className={OVERLINE_CLS}>Fill it in the same day</div>
            <p className="mt-3 text-sm text-ink-muted leading-relaxed">
              Memory fades within a week. A single line, written the same day, is worth ten paragraphs written three months later.
            </p>
          </div>
          <div className="border border-rule bg-linen-alt p-6">
            <div className={OVERLINE_CLS}>Neutral, factual, brief</div>
            <p className="mt-3 text-sm text-ink-muted leading-relaxed">
              Times, names, direct quotes, page numbers. Feelings belong in a diary, not here — a chronology is more powerful when it is dry.
            </p>
          </div>
          <div className="border border-rule bg-linen-alt p-6">
            <div className={OVERLINE_CLS}>Keep a copy off-device</div>
            <p className="mt-3 text-sm text-ink-muted leading-relaxed">
              This one lives in your browser. Print it, or download a .csv, and store a paper copy somewhere safe — the way solicitors do. A downloaded .csv can be imported back on any device.
            </p>
          </div>
        </section>

        <p className="print:hidden mt-10 text-xs text-ink-muted font-accent leading-relaxed max-w-2xl">
          <strong className="text-ink">Where the data lives.</strong> Everything on this page is
          saved only in <em>this browser on this device</em>. No server, no
          account, no analytics on the content. If you clear your browser
          data, or use a different browser, it will not be there.
        </p>
      </article>
    </div>
  );
}
